import React, { useEffect, useMemo, useRef, useState } from "react";
import PizzaSizeModalitiesEditor from "../menu/PizzaSizeModalitiesEditor";
import { getPizzaSaleModes, getPizzaStartingPrice } from "../../services/local/pizzaHalfAndHalf";
import { optimizeImageFile } from "../../services/images/optimizeImage";
import CatalogImage from "../common/CatalogImage";
import MenuProductImage from "../menu/MenuProductImage";
import { useIngredientes } from "../context/IngredientesContext";
import {
  readSessionUiState,
  removeSessionUiState,
  writeSessionUiState,
} from "../../services/local/uiSessionState";
import {
  DEFAULT_STOCK_UNIT,
  STOCK_UNIT_OPTIONS,
  getStockUnitDefinition,
  normalizeStockUnit,
} from "../../services/local/stockUnits";
import {
  getIngredientConsumptionQuantity,
  getIngredientMenuBaseQuantity,
} from "../../services/local/menuIngredientModel";
import {
  buildPizzaHalfAndHalfOptions,
  DEFAULT_PIZZA_SIZE_LABELS,
  getPizzaProductName,
  isReservedPizzaCategoryName,
  normalizePizzaSizeOptions,
} from "../../services/local/pizzaHalfAndHalf";
import {
  DEFAULT_EMPANADA_COOKING_OPTIONS,
  DEFAULT_EMPANADA_SALE_OPTIONS,
  isReservedEmpanadaCategoryName,
  normalizeEmpanadaCookingOptions,
  normalizeEmpanadaSaleOptions,
} from "../../services/local/empanadaSales";

const normalizePriceMode = (value) => {
  if (value === "manual") {
    return "manual";
  }

  if (value === "suggested" || value === "sugerido") {
    return "suggested";
  }

  return "suggested";
};

const DEFAULT_MAX_EXTRA_QUANTITY = 2;

const normalizeSimpleText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const getMenuEntityLabel = (entity) =>
  String(entity?.nombre || entity?.name || entity?.label || "").trim();

const getMenuEntitySelectionValue = (entity) =>
  String(entity?.id || getMenuEntityLabel(entity)).trim();

const menuEntityMatchesSelection = (entity, selection) => {
  const safeSelection = String(selection || "").trim();

  if (!safeSelection) {
    return false;
  }

  const entityId = String(entity?.id || "").trim();
  const entityLabel = getMenuEntityLabel(entity);

  return (
    entityId === safeSelection ||
    entityLabel === safeSelection ||
    normalizeSimpleText(entityLabel) === normalizeSimpleText(safeSelection)
  );
};

const buildPizzaSizeDraftId = (label, index = 0) =>
  normalizeSimpleText(label)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || `pizza-size-${index + 1}`;

const createPizzaSizeDraftFromLabel = (label, index = 0) => ({
  id: buildPizzaSizeDraftId(label, index),
  label,
  fullPrice: 0,
  halfPrice: 0,
  slicePrice: 0,
  slices: 0,
  recipeScale: 1,
  active: true,
  sellWhole: true,
  sellHalf: false,
  allowHalfAndHalf: false,
  sellSlice: false,
});

const buildEmpanadaSaleDraftId = (label, index = 0) =>
  normalizeSimpleText(label)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || `empanada-sale-${index + 1}`;

const createEmpanadaSaleDraftFromPreset = (preset, index = 0) => ({
  id: preset.id || buildEmpanadaSaleDraftId(preset.label, index),
  label: preset.label,
  unitCount: Number(preset.unitCount || 1),
  price: 0,
  active: true,
});

const buildEmpanadaCookingDraftId = (label, index = 0) =>
  normalizeSimpleText(label)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || `empanada-cooking-${index + 1}`;

const createEmpanadaCookingDraftFromPreset = (preset, index = 0) => ({
  id: preset.id || buildEmpanadaCookingDraftId(preset.label, index),
  label: preset.label,
  active: false,
});

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const buildPizzaSizeDraftOptions = (sourceOptions = []) => {
  const source = normalizePizzaSizeOptions(sourceOptions, { includeInactive: true });
  const byKey = new Map();

  source.forEach((option) => {
    byKey.set(normalizeSimpleText(option.label || option.id), option);
  });

  DEFAULT_PIZZA_SIZE_LABELS.forEach((label, index) => {
    const key = normalizeSimpleText(label);

    if (!byKey.has(key)) {
      byKey.set(key, {
        ...createPizzaSizeDraftFromLabel(label, index),
        active: false,
      });
    }
  });

  return Array.from(byKey.values());
};

const compactPizzaSizeOptionsForSave = (options = []) =>
  normalizePizzaSizeOptions(options, { includeInactive: true })
    .filter(
      (option) =>
        option.active !== false
    )
    .map((option) => ({
      id: option.id,
      label: option.label,
      fullPrice: Number(option.fullPrice || 0),
      halfPrice: Number(option.halfPrice || 0),
      slicePrice: Number(option.slicePrice || 0),
      slices: Number(option.slices || 0),
      recipeScale: Number(option.recipeScale || 1),
      active: true,
      sellWhole: option.sellWhole,
      sellHalf: option.sellHalf,
      allowHalfAndHalf: option.allowHalfAndHalf,
      sellSlice: option.sellSlice,
    }));

const buildEmpanadaSaleDraftOptions = (sourceOptions = []) => {
  const source = normalizeEmpanadaSaleOptions(sourceOptions, {
    includeInactive: true,
  });
  const byKey = new Map();

  source.forEach((option) => {
    byKey.set(normalizeSimpleText(option.label || option.id), option);
  });

  DEFAULT_EMPANADA_SALE_OPTIONS.forEach((preset, index) => {
    const key = normalizeSimpleText(preset.label);

    if (!byKey.has(key)) {
      byKey.set(key, {
        ...createEmpanadaSaleDraftFromPreset(preset, index),
        active: false,
      });
    }
  });

  return Array.from(byKey.values());
};

const compactEmpanadaSaleOptionsForSave = (options = []) =>
  normalizeEmpanadaSaleOptions(options, { includeInactive: true })
    .filter(
      (option) =>
        option.active !== false &&
        Number(option.price || 0) > 0 &&
        Number(option.unitCount || 0) > 0
    )
    .map((option) => ({
      id: option.id,
      label: option.label,
      unitCount: Number(option.unitCount || 1),
      price: Number(option.price || 0),
      active: true,
    }));

const buildEmpanadaCookingDraftOptions = (sourceOptions = []) => {
  const source = normalizeEmpanadaCookingOptions(sourceOptions, {
    includeInactive: true,
  });
  const byKey = new Map();

  source.forEach((option) => {
    byKey.set(normalizeSimpleText(option.label || option.id), option);
  });

  DEFAULT_EMPANADA_COOKING_OPTIONS.forEach((preset, index) => {
    const key = normalizeSimpleText(preset.label);

    if (!byKey.has(key)) {
      byKey.set(key, createEmpanadaCookingDraftFromPreset(preset, index));
    }
  });

  return Array.from(byKey.values());
};

const compactEmpanadaCookingOptionsForSave = (options = []) =>
  normalizeEmpanadaCookingOptions(options, { includeInactive: true })
    .filter((option) => option.active !== false && String(option.label || "").trim())
    .map((option) => ({
      id: option.id,
      label: option.label,
      active: true,
    }));

const fileToDataUrl = (file) => optimizeImageFile(file, { profile: "product" });

const normalizeRecipeIngredients = (ingredientes = []) => {
  const mapped = new Map();

  ingredientes.forEach((ingrediente) => {
    if (typeof ingrediente !== "object" || ingrediente === null) {
      if (ingrediente !== null && ingrediente !== undefined) {
        mapped.set(ingrediente, {
          id: ingrediente,
          menuBaseQuantity: 1,
          menuQuantity: 1,
          baseQuantity: 1,
          cantidad: 1,
          consumptionQuantity: 1,
          stockQuantity: 1,
          name: "",
          nombre: "",
          price: 0,
          precio: 0,
          type: "optional",
          tipo: "opcional",
          selectable: true,
          seleccionable: true,
          canRemove: false,
          canAddMore: false,
          maxExtraQuantity: 0,
          unit: DEFAULT_STOCK_UNIT,
          unidad: DEFAULT_STOCK_UNIT,
          consumptionUnit: DEFAULT_STOCK_UNIT,
        });
      }
      return;
    }

    const id = ingrediente.id;
    if (!id) {
      return;
    }

    const menuBaseQuantity = getIngredientMenuBaseQuantity(ingrediente);
    const consumptionQuantity = getIngredientConsumptionQuantity(ingrediente);
    const canAddMore = Boolean(
      ingrediente.canAddMore ?? ingrediente.can_add_more ?? false
    );

    mapped.set(id, {
      id,
      name: ingrediente.name || ingrediente.nombre || "",
      nombre: ingrediente.nombre || ingrediente.name || "",
      price:
        ingrediente.price !== undefined && ingrediente.price !== null
          ? Number(ingrediente.price || 0)
          : Number(ingrediente.precio || 0),
      precio:
        ingrediente.precio !== undefined && ingrediente.precio !== null
          ? Number(ingrediente.precio || 0)
          : Number(ingrediente.price || 0),
      type: ingrediente.type || ingrediente.tipo || "optional",
      tipo: ingrediente.tipo || ingrediente.type || "opcional",
      selectable: ingrediente.selectable ?? ingrediente.seleccionable ?? true,
      seleccionable: ingrediente.seleccionable ?? ingrediente.selectable ?? true,
      menuBaseQuantity,
      menuQuantity: menuBaseQuantity,
      baseQuantity: menuBaseQuantity,
      cantidad: menuBaseQuantity,
      consumptionQuantity,
      stockQuantity: consumptionQuantity,
      canRemove: menuBaseQuantity > 0 && Boolean(
        ingrediente.canRemove ?? ingrediente.can_remove ?? false
      ),
      canAddMore,
      maxExtraQuantity: canAddMore
        ? Math.max(
            1,
            Number(
              ingrediente.maxExtraQuantity ??
                ingrediente.max_extra_quantity ??
                DEFAULT_MAX_EXTRA_QUANTITY
            )
          )
        : 0,
      unit: normalizeStockUnit(
        ingrediente.unit ?? ingrediente.unidad ?? ingrediente.consumptionUnit
      ),
      unidad: normalizeStockUnit(
        ingrediente.unidad ?? ingrediente.unit ?? ingrediente.consumptionUnit
      ),
      consumptionUnit: normalizeStockUnit(
        ingrediente.consumptionUnit ?? ingrediente.unit ?? ingrediente.unidad
      ),
    });
  });

  return Array.from(mapped.values());
};

const sanitizeRecipeIngredient = (config) => {
  const menuBaseQuantity = Math.max(
    0,
    Number((config?.menuBaseQuantity ?? config?.baseQuantity) || 0)
  );
  const consumptionQuantity = Math.max(
    0,
    Number(
      config?.consumptionQuantity ??
        config?.stockQuantity ??
        config?.stockBaseQuantity ??
        0
    )
  );
  const canAddMore = Boolean(config?.canAddMore);

  if (menuBaseQuantity <= 0 && !canAddMore) {
    return null;
  }

  return {
    id: config.id,
    name: config?.name || config?.nombre || "",
    nombre: config?.nombre || config?.name || "",
    price:
      config?.price !== undefined && config?.price !== null
        ? Number(config.price || 0)
        : Number(config?.precio || 0),
    precio:
      config?.precio !== undefined && config?.precio !== null
        ? Number(config.precio || 0)
        : Number(config?.price || 0),
    type: config?.type || config?.tipo || "optional",
    tipo: config?.tipo || config?.type || "opcional",
    selectable: config?.selectable ?? config?.seleccionable ?? true,
    seleccionable: config?.seleccionable ?? config?.selectable ?? true,
    menuBaseQuantity,
    menuQuantity: menuBaseQuantity,
    baseQuantity: menuBaseQuantity,
    cantidad: menuBaseQuantity,
    consumptionQuantity,
    stockQuantity: consumptionQuantity,
    canRemove: menuBaseQuantity > 0 && Boolean(config?.canRemove),
    canAddMore,
    maxExtraQuantity: canAddMore
      ? Math.max(
          1,
          Number(config?.maxExtraQuantity ?? DEFAULT_MAX_EXTRA_QUANTITY)
        )
      : 0,
    unit: normalizeStockUnit(config?.unit ?? config?.unidad ?? config?.consumptionUnit),
    unidad: normalizeStockUnit(config?.unidad ?? config?.unit ?? config?.consumptionUnit),
    consumptionUnit: normalizeStockUnit(
      config?.consumptionUnit ?? config?.unit ?? config?.unidad
    ),
  };
};

const hasMeaningfulRecipeConfig = (ingredientes = []) =>
  Array.isArray(ingredientes) &&
  ingredientes.some(
    (ingrediente) =>
      Number(
        ingrediente?.menuBaseQuantity ??
          ingrediente?.baseQuantity ??
          ingrediente?.cantidad ??
          0
      ) > 0 ||
      Number(
        ingrediente?.consumptionQuantity ??
          ingrediente?.stockQuantity ??
          ingrediente?.quantity ??
          0
      ) > 0 ||
      Boolean(ingrediente?.canAddMore)
  );

const hasMeaningfulDraftState = (draft) => {
  if (!draft || typeof draft !== "object") {
    return false;
  }

  return Boolean(
    String(draft.nombre || "").trim() ||
      String(draft.descripcion || "").trim() ||
      String(draft.categoriaSeleccionada || "").trim() ||
      String(draft.subcategoriaSeleccionada || "").trim() ||
      String(draft.nuevaCategoria || "").trim() ||
      String(draft.nuevaSubcat || "").trim() ||
      String(draft.busquedaIngrediente || "").trim() ||
      String(draft.precioManual || "").trim() ||
      String(draft.nuevoTamanoPizza || "").trim() ||
      draft.foto ||
      (Array.isArray(draft.pizzaSizeOptions) &&
        draft.pizzaSizeOptions.some(
          (option) =>
            option?.active !== false ||
            Number(option?.fullPrice || 0) > 0 ||
            Number(option?.halfPrice || 0) > 0 ||
            Number(option?.slicePrice || 0) > 0
        )) ||
      hasMeaningfulRecipeConfig(draft.ingredientesReceta)
  );
};

const formatRecipeQuantity = (value) => {
  const numeric = Number(value || 0);

  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "0";
  }

  return Number.isInteger(numeric)
    ? String(numeric)
    : String(Number(numeric.toFixed(3)));
};

const getRecipeQuantityStep = (unit) => {
  const definition = getStockUnitDefinition(unit);
  return definition?.family === "count" || definition?.family === "portion"
    ? "1"
    : "0.01";
};

function ModalCrearPlato({
  categorias = [],
  subcategoriasPorCategoria = {},
  platos = [],
  categoriaInicial = "",
  initialData = null,
  draftStorageKey = "",
  onAgregarCategoria,
  onEliminarCategoria,
  onAgregarSubcategoria,
  onEliminarSubcategoria,
  onClose,
  onGuardar,
}) {
  const { ingredientes } = useIngredientes();

  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [foto, setFoto] = useState(null);
  const [processingImage, setProcessingImage] = useState(false);
  const imageRequestRef = useRef(0);
  const [ingredientesReceta, setIngredientesReceta] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(categoriaInicial);
  const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState("");
  const subcategoryEditedRef = useRef(false);
  const [nuevaCategoria, setNuevaCategoria] = useState("");
  const [nuevaSubcat, setNuevaSubcat] = useState("");
  const [busquedaIngrediente, setBusquedaIngrediente] = useState("");
  const [modoPrecio, setModoPrecio] = useState("suggested");
  const [precioManual, setPrecioManual] = useState("");
  const [pizzaSizeDrafts, setPizzaSizeDrafts] = useState(() =>
    buildPizzaSizeDraftOptions()
  );
  const [nuevoTamanoPizza, setNuevoTamanoPizza] = useState("");
  const [empanadaSaleDrafts, setEmpanadaSaleDrafts] = useState(() =>
    buildEmpanadaSaleDraftOptions()
  );
  const [empanadaCookingDrafts, setEmpanadaCookingDrafts] = useState(() =>
    buildEmpanadaCookingDraftOptions()
  );
  const [guardando, setGuardando] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const draftScope = initialData?.id ? `edit:${initialData.id}` : "new";
  const draftHydratedRef = useRef("");
  const draftPersistBlockedRef = useRef(true);

  useEffect(() => {
    setProcessingImage(false);
    return () => { imageRequestRef.current += 1; };
  }, [draftScope, draftStorageKey]);

  const subcategorias = useMemo(() => {
    const todas = [...(subcategoriasPorCategoria[categoriaSeleccionada] || [])];

    if (
      initialData?.subcategoria &&
      categoriaSeleccionada === (initialData.categoria || categoriaInicial || "") &&
      !todas.some((subcategoria) => subcategoria.nombre === initialData.subcategoria)
    ) {
      todas.push({
        id: initialData.subcategoriaId || `subcat-local-${Date.now()}`,
        nombre: initialData.subcategoria,
      });
    }

    return todas;
  }, [categoriaSeleccionada, categoriaInicial, initialData, subcategoriasPorCategoria]);
  const todasLasSubcategorias = useMemo(
    () =>
      Object.values(subcategoriasPorCategoria || {}).reduce(
        (acumuladas, grupo) =>
          Array.isArray(grupo) ? [...acumuladas, ...grupo] : acumuladas,
        []
      ),
    [subcategoriasPorCategoria]
  );

  const categoriaActiva = useMemo(
    () =>
      categorias.find(
        (categoria) => menuEntityMatchesSelection(categoria, categoriaSeleccionada)
      ) || null,
    [categoriaSeleccionada, categorias]
  );

  const subcategoriaActiva = useMemo(
    () =>
      subcategorias.find(
        (subcategoria) =>
          menuEntityMatchesSelection(subcategoria, subcategoriaSeleccionada)
      ) || null,
    [subcategoriaSeleccionada, subcategorias]
  );
  const categoriaActivaNombre =
    getMenuEntityLabel(categoriaActiva) || categoriaSeleccionada || "";
  const subcategoriaActivaNombre =
    getMenuEntityLabel(subcategoriaActiva) || subcategoriaSeleccionada || "";
  const isCategoriaReservadaPizza =
    isReservedPizzaCategoryName(categoriaActivaNombre);
  const isSubcategoriaReservadaPizza =
    isReservedPizzaCategoryName(subcategoriaActivaNombre);
  const isPizzaCategorySelected =
    isCategoriaReservadaPizza || isSubcategoriaReservadaPizza;
  const isCategoriaReservadaEmpanada =
    isReservedEmpanadaCategoryName(categoriaActivaNombre);
  const isSubcategoriaReservadaEmpanada =
    isReservedEmpanadaCategoryName(subcategoriaActivaNombre);
  const isEmpanadaCategorySelected =
    isCategoriaReservadaEmpanada || isSubcategoriaReservadaEmpanada;

  useEffect(() => {
    const savedDraft = readSessionUiState(draftStorageKey, null);
    const hasMatchingDraft =
      savedDraft &&
      savedDraft.scope === draftScope &&
      typeof savedDraft === "object";
    const canHydrateDraft =
      hasMatchingDraft &&
      (!initialData ||
        savedDraft.dirty === true ||
        hasMeaningfulDraftState(savedDraft));

    draftPersistBlockedRef.current = true;
    subcategoryEditedRef.current = Boolean(canHydrateDraft);

    if (hasMatchingDraft && !canHydrateDraft && initialData) {
      removeSessionUiState(draftStorageKey);
    }

    if (canHydrateDraft) {
      setNombre(savedDraft.nombre || "");
      setDescripcion(savedDraft.descripcion || "");
      setFoto(savedDraft.foto || null);
      setIngredientesReceta(normalizeRecipeIngredients(savedDraft.ingredientesReceta || []));
      setCategoriaSeleccionada(savedDraft.categoriaSeleccionada || categoriaInicial || "");
      setSubcategoriaSeleccionada(savedDraft.subcategoriaSeleccionada || "");
      setNuevaCategoria(savedDraft.nuevaCategoria || "");
      setNuevaSubcat(savedDraft.nuevaSubcat || "");
      setBusquedaIngrediente(savedDraft.busquedaIngrediente || "");
      setModoPrecio(normalizePriceMode(savedDraft.modoPrecio));
      setPrecioManual(savedDraft.precioManual || "");
      setPizzaSizeDrafts(buildPizzaSizeDraftOptions(savedDraft.pizzaSizeOptions || []));
      setNuevoTamanoPizza(savedDraft.nuevoTamanoPizza || "");
      setEmpanadaSaleDrafts(
        buildEmpanadaSaleDraftOptions(savedDraft.empanadaSaleOptions || [])
      );
      setEmpanadaCookingDrafts(
        buildEmpanadaCookingDraftOptions(savedDraft.empanadaCookingOptions || [])
      );
      draftHydratedRef.current = `${draftStorageKey}:${draftScope}`;
      return;
    }

    if (initialData) {
      setNombre(initialData.nombre || "");
      setDescripcion(initialData.descripcion || "");
      setFoto(null);
      setIngredientesReceta(
        normalizeRecipeIngredients(initialData.ingredientes || initialData.ingredients || [])
      );
      setCategoriaSeleccionada(initialData.categoria || categoriaInicial || "");
      setSubcategoriaSeleccionada(initialData.subcategoriaId || "");
      setModoPrecio(normalizePriceMode(initialData.modoPrecio));
      setPrecioManual(
        normalizePriceMode(initialData.modoPrecio) === "manual"
          ? String(initialData.precio || "")
          : ""
      );
      setNuevaCategoria("");
      setNuevaSubcat("");
      setBusquedaIngrediente("");
      setPizzaSizeDrafts(
        buildPizzaSizeDraftOptions(
          initialData.pizzaSizeOptions || initialData.pizza_size_options || []
        )
      );
      setNuevoTamanoPizza("");
      setEmpanadaSaleDrafts(
        buildEmpanadaSaleDraftOptions(
          initialData.empanadaSaleOptions || initialData.empanada_sale_options || []
        )
      );
      setEmpanadaCookingDrafts(
        buildEmpanadaCookingDraftOptions(
          initialData.empanadaCookingOptions ||
            initialData.empanada_cooking_options ||
            []
        )
      );
      draftHydratedRef.current = `${draftStorageKey}:${draftScope}`;
      return;
    }

    setNombre("");
    setDescripcion("");
    setFoto(null);
    setIngredientesReceta([]);
    setCategoriaSeleccionada(categoriaInicial || "");
    setSubcategoriaSeleccionada("");
    setBusquedaIngrediente("");
    setModoPrecio("suggested");
    setPrecioManual("");
    setPizzaSizeDrafts(buildPizzaSizeDraftOptions());
    setNuevoTamanoPizza("");
    setEmpanadaSaleDrafts(buildEmpanadaSaleDraftOptions());
    setEmpanadaCookingDrafts(buildEmpanadaCookingDraftOptions());
    setNuevaCategoria("");
    setNuevaSubcat("");
    draftHydratedRef.current = `${draftStorageKey}:${draftScope}`;
  }, [categoriaInicial, draftScope, draftStorageKey, initialData]);

  useEffect(() => {
    if (
      !draftStorageKey ||
      draftHydratedRef.current !== `${draftStorageKey}:${draftScope}`
    ) {
      return;
    }

    if (draftPersistBlockedRef.current) {
      draftPersistBlockedRef.current = false;
      return;
    }

    writeSessionUiState(draftStorageKey, {
      dirty: true,
      scope: draftScope,
      nombre,
      descripcion,
      foto: typeof foto === "string" ? foto : null,
      ingredientesReceta,
      categoriaSeleccionada,
      subcategoriaSeleccionada,
      nuevaCategoria,
      nuevaSubcat,
      busquedaIngrediente,
      modoPrecio,
      precioManual,
      pizzaSizeOptions: pizzaSizeDrafts,
      nuevoTamanoPizza,
      empanadaSaleOptions: empanadaSaleDrafts,
      empanadaCookingOptions: empanadaCookingDrafts,
    });
  }, [
    busquedaIngrediente,
    categoriaSeleccionada,
    descripcion,
    draftScope,
    draftStorageKey,
    empanadaCookingDrafts,
    empanadaSaleDrafts,
    foto,
    ingredientesReceta,
    modoPrecio,
    nombre,
    nuevaCategoria,
    nuevaSubcat,
    nuevoTamanoPizza,
    precioManual,
    pizzaSizeDrafts,
    subcategoriaSeleccionada,
  ]);

  useEffect(() => {
    if (
      !subcategoryEditedRef.current &&
      initialData?.subcategoria &&
      categoriaSeleccionada === (initialData.categoria || categoriaInicial || "") &&
      !subcategoriaSeleccionada &&
      Array.isArray(subcategorias) &&
      subcategorias.length > 0
    ) {
      const match = subcategorias.find(
        (subcategoria) =>
          normalizeSimpleText(getMenuEntityLabel(subcategoria)) ===
          normalizeSimpleText(initialData.subcategoria)
      );

      if (match?.id) {
        setSubcategoriaSeleccionada(match.id);
      }
    }
  }, [initialData, categoriaInicial, categoriaSeleccionada, subcategoriaSeleccionada, subcategorias]);

  const previewImagen = useMemo(() => {
    if (foto instanceof File) {
      return URL.createObjectURL(foto);
    }

    if (typeof foto === "string" && foto) {
      return foto;
    }

    return initialData?.fotoUrl || null;
  }, [foto, initialData]);

  const recipeConfigById = useMemo(
    () =>
      ingredientesReceta.reduce((acc, config) => {
        acc[config.id] = config;
        return acc;
      }, {}),
    [ingredientesReceta]
  );

  const precioSugerido = useMemo(
    () =>
      ingredientesReceta.reduce((total, receta) => {
        const ingrediente = ingredientes.find((item) => item.id === receta.id);
        return (
          total +
          Number(ingrediente?.precio || 0) * Number(receta.menuBaseQuantity || 0)
        );
      }, 0),
    [ingredientes, ingredientesReceta]
  );

  const activePizzaSizeOptions = useMemo(
    () => compactPizzaSizeOptionsForSave(pizzaSizeDrafts),
    [pizzaSizeDrafts]
  );
  const activeEmpanadaSaleOptions = useMemo(
    () => compactEmpanadaSaleOptionsForSave(empanadaSaleDrafts),
    [empanadaSaleDrafts]
  );
  const activeEmpanadaCookingOptions = useMemo(
    () => compactEmpanadaCookingOptionsForSave(empanadaCookingDrafts),
    [empanadaCookingDrafts]
  );
  const precioFinal = modoPrecio === "manual" ? Number(precioManual || 0) : precioSugerido;
  const pizzaStartingPrice = activePizzaSizeOptions.length
    ? Math.min(...activePizzaSizeOptions.map(getPizzaStartingPrice))
    : precioFinal;
  const empanadaStartingPrice = activeEmpanadaSaleOptions.length
    ? Math.min(...activeEmpanadaSaleOptions.map((option) => Number(option.price || 0)))
    : precioFinal;

  const pizzaDraftProduct = useMemo(
    () => ({
      id: initialData?.id || "__pizza_draft__",
      active: initialData?.active !== false,
      nombre: nombre.trim() || "Nueva pizza",
      name: nombre.trim() || "Nueva pizza",
      categoria: categoriaSeleccionada,
      categoryId: categoriaSeleccionada,
      categoryName: categoriaActivaNombre,
      subcategoriaId: subcategoriaSeleccionada,
      subcategoryId: subcategoriaSeleccionada,
      subcategoria: subcategoriaActiva?.nombre || "",
      subcategoryName: subcategoriaActiva?.nombre || "",
      precio: pizzaStartingPrice,
      price: pizzaStartingPrice,
      precioSugerido,
      suggestedPrice: precioSugerido,
      pizzaSizeOptions: activePizzaSizeOptions,
      pizza_size_options: activePizzaSizeOptions,
    }),
    [
      activePizzaSizeOptions,
      categoriaActivaNombre,
      categoriaSeleccionada,
      initialData?.active,
      initialData?.id,
      nombre,
      pizzaStartingPrice,
      precioSugerido,
      subcategoriaActiva?.nombre,
      subcategoriaSeleccionada,
    ]
  );

  const pizzaHalfAndHalfOptions = useMemo(
    () =>
      isPizzaCategorySelected
        ? buildPizzaHalfAndHalfOptions(pizzaDraftProduct, platos, categorias, {
            subcategories: todasLasSubcategorias,
          })
        : [],
    [
      categorias,
      isPizzaCategorySelected,
      pizzaDraftProduct,
      platos,
      todasLasSubcategorias,
    ]
  );

  const totalIngredientesBase = ingredientesReceta.filter(
    (ingrediente) => Number(ingrediente.menuBaseQuantity || 0) > 0
  ).length;
  const totalIngredientesExtra = ingredientesReceta.filter(
    (ingrediente) => ingrediente.canAddMore
  ).length;

  const recipeSummaryItems = useMemo(
    () =>
      ingredientesReceta
        .filter(
          (ingrediente) =>
            Number(ingrediente.menuBaseQuantity || 0) > 0 || ingrediente.canAddMore
        )
        .map((ingrediente) => {
          const ingredienteBase =
            ingredientes.find((item) => item.id === ingrediente.id) || null;
          const quantityLabel =
            Number(ingrediente.consumptionQuantity || 0) > 0
              ? `Menu: ${formatRecipeQuantity(
                  ingrediente.menuBaseQuantity || 0
                )} · Stock: ${formatRecipeQuantity(
                  ingrediente.consumptionQuantity || 0
                )} ${
                  ingrediente.consumptionUnit ||
                  ingrediente.unit ||
                  ingrediente.unidad ||
                  DEFAULT_STOCK_UNIT
                }`
              : "Sin consumo";

          return {
            id: ingrediente.id,
            nombre:
              ingredienteBase?.nombre ||
              ingrediente.name ||
              ingrediente.nombre ||
              "Ingrediente",
            quantityLabel,
            canAddMore: Boolean(ingrediente.canAddMore),
            canRemove: Boolean(ingrediente.canRemove),
          };
        })
        .sort((left, right) => left.nombre.localeCompare(right.nombre)),
    [ingredientes, ingredientesReceta]
  );

  const ingredientesFiltrados = useMemo(() => {
    const termino = busquedaIngrediente.trim().toLowerCase();

    return [...ingredientes]
      .filter((ingrediente) =>
        termino ? ingrediente.nombre.toLowerCase().includes(termino) : true
      )
      .sort((a, b) => {
        const configA = recipeConfigById[a.id];
        const configB = recipeConfigById[b.id];
        const weightA =
          (Number(configA?.baseQuantity || 0) > 0 ? 2 : 0) +
          (configA?.canAddMore ? 1 : 0);
        const weightB =
          (Number(configB?.baseQuantity || 0) > 0 ? 2 : 0) +
          (configB?.canAddMore ? 1 : 0);

        if (weightA !== weightB) {
          return weightB - weightA;
        }

        return a.nombre.localeCompare(b.nombre);
      });
  }, [busquedaIngrediente, ingredientes, recipeConfigById]);

  const setRecipeIngredient = (id, updater) => {
    setIngredientesReceta((prev) => {
      const current =
        prev.find((ingrediente) => ingrediente.id === id) || {
          id,
          menuBaseQuantity: 0,
          menuQuantity: 0,
          baseQuantity: 0,
          cantidad: 0,
          consumptionQuantity: 0,
          stockQuantity: 0,
          canRemove: false,
          canAddMore: false,
          maxExtraQuantity: 0,
        };

      const nextValue = sanitizeRecipeIngredient(
        typeof updater === "function" ? updater(current) : updater
      );

      const rest = prev.filter((ingrediente) => ingrediente.id !== id);
      return nextValue ? [...rest, nextValue] : rest;
    });
  };

  const updateConsumptionQuantity = (id, rawValue) => {
    const normalized = String(rawValue ?? "").replace(",", ".").trim();
    const parsedValue = normalized === "" ? 0 : Number(normalized);

    if (!Number.isFinite(parsedValue)) {
      return;
    }

    const nextConsumptionQuantity = Math.max(0, parsedValue);

    setRecipeIngredient(id, (current) => ({
      ...current,
      consumptionQuantity: nextConsumptionQuantity,
      stockQuantity: nextConsumptionQuantity,
    }));
  };

  const updateMenuBaseQuantity = (id, rawValue) => {
    const normalized = String(rawValue ?? "").replace(",", ".").trim();
    const parsedValue = normalized === "" ? 0 : Number(normalized);

    if (!Number.isFinite(parsedValue)) {
      return;
    }

    const nextMenuBaseQuantity = Math.max(0, parsedValue);

    setRecipeIngredient(id, (current) => ({
      ...current,
      menuBaseQuantity: nextMenuBaseQuantity,
      menuQuantity: nextMenuBaseQuantity,
      baseQuantity: nextMenuBaseQuantity,
      cantidad: nextMenuBaseQuantity,
      canRemove: nextMenuBaseQuantity > 0 ? current.canRemove : false,
    }));
  };

  const toggleCanRemove = (id, checked) => {
    setRecipeIngredient(id, (current) => ({
      ...current,
      canRemove: Number(current.menuBaseQuantity || 0) > 0 && checked,
    }));
  };

  const toggleCanAddMore = (id, checked) => {
    setRecipeIngredient(id, (current) => ({
      ...current,
      canAddMore: checked,
      maxExtraQuantity: checked
        ? Number(current.maxExtraQuantity || DEFAULT_MAX_EXTRA_QUANTITY)
        : 0,
    }));
  };

  const updateMaxExtraQuantity = (id, value) => {
    setRecipeIngredient(id, (current) => ({
      ...current,
      maxExtraQuantity: current.canAddMore
        ? Math.max(1, Number(value || DEFAULT_MAX_EXTRA_QUANTITY))
        : 0,
    }));
  };

  const agregarSubcategoria = async () => {
    if (!categoriaSeleccionada) {
      alert("Seleccione una categoria antes de agregar una subcategoria");
      return;
    }

    const nueva = nuevaSubcat.trim();
    if (!nueva) {
      return;
    }

    if (
      isCategoriaReservadaPizza &&
      isReservedPizzaCategoryName(nueva)
    ) {
      alert(
        "Si la categoria ya es Pizza/Pizzas, usa una subcategoria de tamano o grupo, por ejemplo Familiar, Grande, Molde u Horno."
      );
      return;
    }

    if (
      isCategoriaReservadaEmpanada &&
      isReservedEmpanadaCategoryName(nueva)
    ) {
      alert(
        "Si la categoria ya es Empanadas, usa una subcategoria de tipo o coccion, por ejemplo Carne, Pollo, Fritas u Horno."
      );
      return;
    }

    const agregada =
      typeof onAgregarSubcategoria === "function"
        ? await onAgregarSubcategoria(categoriaSeleccionada, nueva)
        : false;

    if (!agregada) {
      return;
    }

    subcategoryEditedRef.current = true;
    setSubcategoriaSeleccionada(agregada.id || "");
    setNuevaSubcat("");
  };

  const agregarCategoria = async () => {
    const nombreCategoria = nuevaCategoria.trim();
    if (!nombreCategoria) {
      return;
    }

    const categoriaId =
      typeof onAgregarCategoria === "function"
        ? await onAgregarCategoria(nombreCategoria)
        : null;

    if (categoriaId) {
      subcategoryEditedRef.current = true;
      setCategoriaSeleccionada(categoriaId.id || categoriaId);
      setSubcategoriaSeleccionada("");
      setNuevaCategoria("");
    }
  };

  const eliminarCategoria = async () => {
    if (!categoriaSeleccionada) {
      alert("Seleccione una categoria para eliminar");
      return;
    }

    const eliminada =
      typeof onEliminarCategoria === "function"
        ? await onEliminarCategoria(categoriaSeleccionada)
        : false;

    if (!eliminada) {
      alert("No se puede eliminar una categoria que ya tiene platos cargados");
      return;
    }

    subcategoryEditedRef.current = true;
    setCategoriaSeleccionada("");
    setSubcategoriaSeleccionada("");
  };

  const eliminarSubcategoria = async () => {
    if (!categoriaSeleccionada) {
      alert("Seleccione una categoria antes de eliminar una subcategoria");
      return;
    }

    if (!subcategoriaSeleccionada) {
      alert("Seleccione una subcategoria para eliminar");
      return;
    }

    const eliminada =
      typeof onEliminarSubcategoria === "function"
        ? await onEliminarSubcategoria(categoriaSeleccionada, subcategoriaSeleccionada)
        : false;

    if (!eliminada) {
      alert("No se puede eliminar una subcategoria que ya tiene platos cargados");
      return;
    }

    subcategoryEditedRef.current = true;
    setSubcategoriaSeleccionada("");
  };

  const activarPrecioManualPizza = () => {
    setModoPrecio("manual");

    if (!precioManual && Number(precioFinal || 0) > 0) {
      setPrecioManual(String(Number(precioFinal || 0).toFixed(2)));
    }
  };

  const actualizarTamanoPizza = (index, cambios = {}) => {
    setPizzaSizeDrafts((actuales) =>
      actuales.map((opcion, opcionIndex) => {
        if (opcionIndex !== index) {
          return opcion;
        }

        const siguiente = { ...opcion, ...cambios };

        if (Object.prototype.hasOwnProperty.call(cambios, "label")) {
          siguiente.id = buildPizzaSizeDraftId(cambios.label, index);
        }

        return siguiente;
      })
    );
  };

  const actualizarPresentacionEmpanada = (index, cambios = {}) => {
    setEmpanadaSaleDrafts((actuales) =>
      actuales.map((opcion, opcionIndex) => {
        if (opcionIndex !== index) {
          return opcion;
        }

        const siguiente = { ...opcion, ...cambios };

        if (Object.prototype.hasOwnProperty.call(cambios, "label")) {
          siguiente.id = buildEmpanadaSaleDraftId(cambios.label, index);
        }

        return siguiente;
      })
    );
  };

  const actualizarCoccionEmpanada = (index, cambios = {}) => {
    setEmpanadaCookingDrafts((actuales) =>
      actuales.map((opcion, opcionIndex) => {
        if (opcionIndex !== index) {
          return opcion;
        }

        const siguiente = { ...opcion, ...cambios };

        if (Object.prototype.hasOwnProperty.call(cambios, "label")) {
          siguiente.id = buildEmpanadaCookingDraftId(cambios.label, index);
        }

        return siguiente;
      })
    );
  };

  const agregarTamanoPizzaManual = () => {
    const label = nuevoTamanoPizza.trim();

    if (!label) {
      return;
    }

    const key = normalizeSimpleText(label);
    const alreadyExists = pizzaSizeDrafts.some(
      (opcion) => normalizeSimpleText(opcion.label) === key
    );

    if (alreadyExists) {
      setPizzaSizeDrafts((actuales) =>
        actuales.map((opcion) =>
          normalizeSimpleText(opcion.label) === key
            ? { ...opcion, active: true }
            : opcion
        )
      );
      setNuevoTamanoPizza("");
      return;
    }

    setPizzaSizeDrafts((actuales) => [
      ...actuales,
      createPizzaSizeDraftFromLabel(label, actuales.length),
    ]);
    setNuevoTamanoPizza("");
  };

  const handleGuardar = async () => {
    if (guardando || processingImage) return;
    if (!nombre.trim()) {
      alert("El nombre del plato es obligatorio");
      return;
    }

    if (!categoriaSeleccionada) {
      alert("Seleccione una categoria");
      return;
    }

    if (isCategoriaReservadaPizza && isSubcategoriaReservadaPizza) {
      setSaveError(
        "La palabra Pizza/Pizzas debe estar solo en categoria o solo en subcategoria. Si la categoria ya es Pizza/Pizzas, usa una subcategoria como Familiar, Grande, Molde u Horno."
      );
      return;
    }

    if (isCategoriaReservadaEmpanada && isSubcategoriaReservadaEmpanada) {
      setSaveError(
        "La palabra Empanada/Empanadas debe estar solo en categoria o solo en subcategoria. Si la categoria ya es Empanadas, usa una subcategoria como Carne, Pollo, Fritas u Horno."
      );
      return;
    }

    if (isPizzaCategorySelected && isEmpanadaCategorySelected) {
      setSaveError(
        "No mezcles Pizzas y Empanadas en el mismo plato. Cada palabra reservada activa reglas de venta distintas."
      );
      return;
    }

    const pizzaOptionsToSave = isPizzaCategorySelected
      ? activePizzaSizeOptions
      : [];
    const hasPizzaPresentationPrices = pizzaOptionsToSave.length > 0;
    const empanadaOptionsToSave = isEmpanadaCategorySelected
      ? activeEmpanadaSaleOptions
      : [];
    const empanadaCookingOptionsToSave = isEmpanadaCategorySelected
      ? activeEmpanadaCookingOptions
      : [];
    const hasEmpanadaPresentationPrices = empanadaOptionsToSave.length > 0;
    const hasEmpanadaCookingOptions = empanadaCookingOptionsToSave.length > 0;
    const hasConfiguredPresentationPrices =
      hasPizzaPresentationPrices || hasEmpanadaPresentationPrices;

    if (modoPrecio === "manual" && !precioManual && !hasConfiguredPresentationPrices) {
      alert("Ingrese un precio manual para el plato");
      return;
    }

    if (isPizzaCategorySelected && !hasPizzaPresentationPrices) {
      setSaveError(
        "Activa al menos un tamano de pizza y carga su precio antes de guardar."
      );
      return;
    }

    if (isEmpanadaCategorySelected && !hasEmpanadaPresentationPrices) {
      setSaveError(
        "Activa al menos una presentacion de empanadas y carga su precio antes de guardar."
      );
      return;
    }

    if (isEmpanadaCategorySelected && !hasEmpanadaCookingOptions) {
      setSaveError(
        "Activa al menos una coccion de empanadas: Al horno, Frita o ambas."
      );
      return;
    }

    const invalidPizzaOption = pizzaOptionsToSave.find(
      (option) =>
        !String(option.label || "").trim() ||
        !getPizzaSaleModes(option).length ||
        (option.sellWhole && Number(option.fullPrice || 0) <= 0) ||
        ((option.sellHalf || option.allowHalfAndHalf) && Number(option.halfPrice || 0) <= 0) ||
        (option.sellSlice && (Number(option.slicePrice || 0) <= 0 || Number(option.slices || 0) <= 0))
    );

    if (invalidPizzaOption) {
      setSaveError(
        "Cada tamano activo necesita al menos una modalidad habilitada y su precio. Para vender por porcion, indica tambien cuantas porciones tiene la pizza."
      );
      return;
    }

    const invalidEmpanadaOption = empanadaOptionsToSave.find(
      (option) =>
        !String(option.label || "").trim() ||
        Number(option.price || 0) <= 0 ||
        Number(option.unitCount || 0) <= 0
    );

    if (invalidEmpanadaOption) {
      setSaveError(
        "Cada presentacion activa de empanadas necesita nombre, cantidad de unidades y precio."
      );
      return;
    }

    if (typeof onGuardar !== "function") {
      return;
    }

    setSaveError("");

    const invalidConfiguredIngredient = ingredientesReceta.find((ingrediente) => {
      const menuBaseQuantity = Number(ingrediente.menuBaseQuantity || 0);
      const consumptionQuantity = Number(ingrediente.consumptionQuantity || 0);
      return (menuBaseQuantity > 0 || ingrediente.canAddMore) && consumptionQuantity <= 0;
    });

    if (invalidConfiguredIngredient) {
      const invalidIngredientBase =
        ingredientes.find((item) => item.id === invalidConfiguredIngredient.id) || null;
      setSaveError(
        `Carga el consumo de stock para ${
          invalidIngredientBase?.nombre || invalidConfiguredIngredient.nombre || "ese ingrediente"
        }.`
      );
      return;
    }

    const receta = ingredientesReceta
      .map(sanitizeRecipeIngredient)
      .filter(Boolean)
      .map((ingrediente) => {
        const ingredienteBase =
          ingredientes.find((item) => item.id === ingrediente.id) || null;

        return {
          ...ingrediente,
          name: ingredienteBase?.nombre || ingrediente.name || ingrediente.nombre || "",
          nombre: ingredienteBase?.nombre || ingrediente.nombre || ingrediente.name || "",
          price: Number(ingredienteBase?.precio || ingrediente.price || ingrediente.precio || 0),
          precio: Number(ingredienteBase?.precio || ingrediente.precio || ingrediente.price || 0),
          type:
            ingredienteBase?.tipo === "obligatorio"
              ? "required"
              : ingrediente.type || ingrediente.tipo || "optional",
          tipo: ingredienteBase?.tipo || ingrediente.tipo || "opcional",
          selectable:
            ingredienteBase?.seleccionable ?? ingrediente.selectable ?? ingrediente.seleccionable ?? true,
          seleccionable:
            ingredienteBase?.seleccionable ?? ingrediente.seleccionable ?? ingrediente.selectable ?? true,
          menuBaseQuantity: Number(ingrediente.menuBaseQuantity || 0),
          menuQuantity: Number(ingrediente.menuBaseQuantity || 0),
          baseQuantity: Number(ingrediente.menuBaseQuantity || 0),
          cantidad: Number(ingrediente.menuBaseQuantity || 0),
          consumptionQuantity: Number(ingrediente.consumptionQuantity || 0),
          stockQuantity: Number(ingrediente.consumptionQuantity || 0),
          unit: ingrediente.consumptionUnit || ingrediente.unit || ingrediente.unidad,
          unidad: ingrediente.consumptionUnit || ingrediente.unit || ingrediente.unidad,
          consumptionUnit:
            ingrediente.consumptionUnit || ingrediente.unit || ingrediente.unidad,
        };
      });

    setGuardando(true);

    const finalPriceToSave = hasPizzaPresentationPrices
      ? Math.min(
          ...pizzaOptionsToSave
            .map(getPizzaStartingPrice)
            .filter((price) => price > 0)
        )
      : hasEmpanadaPresentationPrices
        ? Math.min(
            ...empanadaOptionsToSave
              .map((option) => Number(option.price || 0))
              .filter((price) => price > 0)
          )
        : precioFinal;

    try {
      const saved = await onGuardar({
        id: initialData?.id,
        active: initialData?.active !== false,
        visibleInMenu: (initialData?.visibleInMenu ?? initialData?.visible_in_menu ?? initialData?.active) !== false,
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
        foto: foto instanceof File ? foto : null,
        fotoUrl:
          (typeof foto === "string" && foto) || initialData?.fotoUrl || null,
        imageChanged: typeof foto === "string" && Boolean(foto),
        ingredientes: receta,
        categoria: categoriaSeleccionada,
        subcategoriaId: subcategoriaSeleccionada,
        subcategoria: getMenuEntityLabel(subcategoriaActiva),
        modoPrecio: hasConfiguredPresentationPrices ? "manual" : modoPrecio,
        precio: finalPriceToSave,
        precioSugerido,
        pizzaSizeOptions: pizzaOptionsToSave,
        empanadaSaleOptions: empanadaOptionsToSave,
        empanadaCookingOptions: empanadaCookingOptionsToSave,
      });

      if (saved === false) {
        setSaveError("No pudimos guardar el plato. Revisa los datos y vuelve a intentar.");
      }
    } catch (error) {
      setSaveError(error?.message || "No pudimos guardar el plato.");
    } finally {
      setGuardando(false);
    }
  };

  const handleFotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    const request = ++imageRequestRef.current;
    setProcessingImage(true);
    try {
      const image = await fileToDataUrl(file);
      if (request === imageRequestRef.current) setFoto(image);
    } catch (error) {
      if (request === imageRequestRef.current) alert(error.message || "No se pudo leer la imagen del plato.");
    } finally {
      if (request === imageRequestRef.current) setProcessingImage(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <h3 style={{ marginTop: 0 }}>
          {initialData ? "Editar Plato" : "Crear Nuevo Plato"}
        </h3>

        <input
          type="text"
          placeholder="Nombre del plato"
          value={nombre}
          onChange={(event) => setNombre(event.target.value)}
          style={styles.input}
        />

        <input
          type="file"
          accept="image/*"
          aria-label="Imagen del plato"
          disabled={processingImage || guardando}
          onChange={handleFotoChange}
          style={styles.input}
        />

        {processingImage && <p role="status">Optimizando imagen...</p>}
        <p>Foto opcional. Si no cargas una, se muestra la imagen predeterminada.</p>
        {!previewImagen && initialData?.imageDeferred ? (
          <MenuProductImage product={initialData} style={styles.previewImagen} />
        ) : (
          <CatalogImage src={previewImagen} alt="Vista previa" style={styles.previewImagen} />
        )}

        <textarea
          placeholder="Descripcion"
          value={descripcion}
          onChange={(event) => setDescripcion(event.target.value)}
          rows={4}
          style={styles.textarea}
        />

        <div style={{ marginBottom: 10 }}>
          <label htmlFor="dish-category">Categoria:</label>
          <select
            id="dish-category"
            value={categoriaSeleccionada}
            onChange={(event) => {
              subcategoryEditedRef.current = true;
              setCategoriaSeleccionada(event.target.value);
              setSubcategoriaSeleccionada("");
            }}
            style={styles.select}
          >
            <option value="">--Seleccione--</option>
            {categorias.map((cat) => (
              <option key={cat.id || cat.nombre} value={cat.id || cat.nombre}>
                {cat.nombre}
              </option>
            ))}
          </select>

          <div style={styles.row}>
            <input
              type="text"
              value={nuevaCategoria}
              placeholder="Agregar categoria"
              onChange={(event) => setNuevaCategoria(event.target.value)}
              style={{ ...styles.input, flex: 1, marginBottom: 0 }}
            />
            <button type="button" onClick={agregarCategoria} style={styles.btnAdd}>
              + Cat
            </button>
            <button type="button" onClick={eliminarCategoria} style={styles.btnDelete}>
              Eliminar
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label htmlFor="dish-subcategory">Subcategoria (opcional):</label>
          <select
            id="dish-subcategory"
            value={subcategoriaSeleccionada}
            onChange={(event) => {
              subcategoryEditedRef.current = true;
              setSubcategoriaSeleccionada(event.target.value);
            }}
            disabled={!categoriaSeleccionada}
            style={styles.select}
          >
            <option value="">Sin subcategoria</option>
            {subcategorias.map((subcategoria) => (
              <option
                key={subcategoria.id || getMenuEntityLabel(subcategoria)}
                value={getMenuEntitySelectionValue(subcategoria)}
              >
                {getMenuEntityLabel(subcategoria)}
              </option>
            ))}
          </select>

          <div style={styles.row}>
            <input
              type="text"
              value={nuevaSubcat}
              placeholder="Agregar subcategoria"
              onChange={(event) => setNuevaSubcat(event.target.value)}
              style={{ ...styles.input, flex: 1, marginBottom: 0 }}
            />
            <button type="button" onClick={agregarSubcategoria} style={styles.btnAdd}>
              + Sub
            </button>
            <button type="button" onClick={eliminarSubcategoria} style={styles.btnDelete}>
              Eliminar
            </button>
          </div>
        </div>

        {isPizzaCategorySelected ? (
          <div style={styles.pizzaMagicBox}>
            <div style={styles.pizzaMagicHeader}>
              <div>
                <span style={styles.pizzaMagicKicker}>
                  Categoria o subcategoria reservada
                </span>
                <h4 style={styles.pizzaMagicTitle}>
                  Pizzas: presentaciones, medias y porciones
                </h4>
                <p style={styles.pizzaMagicText}>
                  Activa los tamanos que ofreces y elige sus modalidades por separado.
                  Activar un tamano no habilita medias, porciones ni mitad y mitad.
                </p>
              </div>
              <span style={styles.pizzaMagicBadge}>Configurable</span>
            </div>

            <div style={styles.pizzaPricingPanel}>
              <div style={styles.pizzaPricingHeader}>
                <strong style={styles.pizzaMagicCardTitle}>
                  Presentaciones y precios
                </strong>
                <span style={styles.pizzaHalfStatus}>
                  {activePizzaSizeOptions.length > 0
                    ? `${activePizzaSizeOptions.length} activos`
                    : "Activa al menos un tamano"}
                </span>
              </div>

              <PizzaSizeModalitiesEditor options={pizzaSizeDrafts} onChange={actualizarTamanoPizza} />

              <div style={styles.pizzaAddSizeRow}>
                <input
                  type="text"
                  value={nuevoTamanoPizza}
                  onChange={(event) => setNuevoTamanoPizza(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      agregarTamanoPizzaManual();
                    }
                  }}
                  style={{ ...styles.input, marginBottom: 0 }}
                  placeholder="Agregar otro tamano, por ejemplo Molde o XL"
                />
                <button
                  type="button"
                  style={styles.pizzaSecondaryButton}
                  onClick={agregarTamanoPizzaManual}
                >
                  + Tamano
                </button>
              </div>

              <p style={styles.pizzaMagicText}>
                El precio visible muestra el menor precio de las modalidades
                habilitadas. Los campos inactivos no se ofrecen al cliente.
              </p>
            </div>

            <div style={styles.pizzaMagicGrid}>
              <div style={styles.pizzaMagicCard}>
                <strong style={styles.pizzaMagicCardTitle}>Mitad y mitad</strong>
                <p style={styles.pizzaMagicText}>
                  Solo aparece si ambos sabores permiten mitad y mitad en el mismo
                  tamano. Vender media pizza no habilita esta combinacion.
                </p>
                <div style={styles.pizzaHalfStatus}>
                  {pizzaHalfAndHalfOptions.length > 0
                    ? `${pizzaHalfAndHalfOptions.length} pizzas compatibles`
                    : activePizzaSizeOptions.some((option) => option.allowHalfAndHalf)
                      ? "Sin otra pizza habilitada para combinar"
                      : "Combinacion deshabilitada"}
                </div>
                {pizzaHalfAndHalfOptions.length > 0 ? (
                  <div style={styles.pizzaHalfPreview}>
                    {pizzaHalfAndHalfOptions.slice(0, 3).map((option) => (
                      <span key={option.id || option.productId}>
                        1/2 {getPizzaProductName(option)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              <div style={styles.pizzaMagicCard}>
                <strong style={styles.pizzaMagicCardTitle}>Precio base</strong>
                <p style={styles.pizzaMagicText}>
                  Para pizzas con presentaciones, el precio manual general queda solo
                  como respaldo. El cobro usa las filas de arriba.
                </p>
                <div style={styles.pizzaPriceRow}>
                  <span style={styles.pizzaPriceValue}>
                    {formatMoney(pizzaStartingPrice)}
                  </span>
                  <button
                    type="button"
                    style={styles.pizzaSecondaryButton}
                    onClick={activarPrecioManualPizza}
                  >
                    Editar respaldo
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {isEmpanadaCategorySelected ? (
          <div style={styles.pizzaMagicBox}>
            <div style={styles.pizzaMagicHeader}>
              <div>
                <span style={styles.pizzaMagicKicker}>
                  Categoria o subcategoria reservada
                </span>
                <h4 style={styles.pizzaMagicTitle}>
                  Empanadas: unidad, media docena y docena
                </h4>
                <p style={styles.pizzaMagicText}>
                  Carga una sola empanada y marca las presentaciones que vende. La
                  receta se toma como una empanada; media docena descuenta 6 y docena
                  descuenta 12.
                </p>
              </div>
              <span style={styles.pizzaMagicBadge}>Automatico</span>
            </div>

            <div style={styles.pizzaPricingPanel}>
              <div style={styles.pizzaPricingHeader}>
                <strong style={styles.pizzaMagicCardTitle}>
                  Presentaciones y precios
                </strong>
                <span style={styles.pizzaHalfStatus}>
                  {activeEmpanadaSaleOptions.length > 0
                    ? `${activeEmpanadaSaleOptions.length} activos`
                    : "Activa al menos una presentacion"}
                </span>
              </div>

              <div style={styles.empanadaSaleTableHeader}>
                <span>Activo</span>
                <span>Presentacion</span>
                <span>Unidades</span>
                <span>Precio</span>
              </div>

              <div style={styles.pizzaSizeRows}>
                {empanadaSaleDrafts.map((opcion, index) => (
                  <div
                    key={`${opcion.id || opcion.label}:${index}`}
                    style={{
                      ...styles.empanadaSaleRow,
                      ...(opcion.active === false ? styles.pizzaSizeRowInactive : {}),
                    }}
                  >
                    <label style={styles.pizzaSizeCheckLabel}>
                      <input
                        type="checkbox"
                        checked={opcion.active !== false}
                        onChange={(event) =>
                          actualizarPresentacionEmpanada(index, {
                            active: event.target.checked,
                          })
                        }
                      />
                    </label>
                    <strong style={styles.empanadaSaleLabel}>
                      {opcion.label}
                    </strong>
                    <span style={styles.empanadaSaleUnits}>
                      {Number(opcion.unitCount || 1)}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={opcion.price || ""}
                      onChange={(event) =>
                        actualizarPresentacionEmpanada(index, {
                          price: event.target.value,
                          active: true,
                        })
                      }
                      style={styles.pizzaSizeInput}
                      placeholder="$ precio"
                    />
                  </div>
                ))}
              </div>

              <p style={styles.pizzaMagicText}>
                El precio visible del plato queda como “desde” el menor precio activo.
                Si no vendes por unidad, desactiva Unidad y deja solo Media docena o
                Docena.
              </p>
            </div>

            <div style={styles.pizzaPricingPanel}>
              <div style={styles.pizzaPricingHeader}>
                <strong style={styles.pizzaMagicCardTitle}>
                  Cocciones ofrecidas
                </strong>
                <span style={styles.pizzaHalfStatus}>
                  {activeEmpanadaCookingOptions.length > 0
                    ? `${activeEmpanadaCookingOptions.length} activas`
                    : "Activa al menos una coccion"}
                </span>
              </div>

              <div style={styles.empanadaCookingGrid}>
                {empanadaCookingDrafts.map((opcion, index) => (
                  <label
                    key={`${opcion.id || opcion.label}:${index}`}
                    style={{
                      ...styles.empanadaCookingCard,
                      ...(opcion.active !== false
                        ? styles.empanadaCookingCardActive
                        : {}),
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={opcion.active !== false}
                      onChange={(event) =>
                        actualizarCoccionEmpanada(index, {
                          active: event.target.checked,
                        })
                      }
                    />
                    <span>
                      Ofrecer {String(opcion.label || "").toLowerCase()}
                    </span>
                  </label>
                ))}
              </div>

              <p style={styles.pizzaMagicText}>
                La receta se carga para 1 empanada. El sistema multiplica el consumo
                por Unidad x1, Media docena x6 o Docena x12. La coccion queda como
                detalle operativo del pedido.
              </p>
            </div>

            <div style={styles.pizzaMagicGrid}>
              <div style={styles.pizzaMagicCard}>
                <strong style={styles.pizzaMagicCardTitle}>Cobro claro</strong>
                <p style={styles.pizzaMagicText}>
                  El comensal elige la presentacion antes de agregar al pedido. No se
                  combinan automaticamente sabores de empanadas.
                </p>
              </div>

              <div style={styles.pizzaMagicCard}>
                <strong style={styles.pizzaMagicCardTitle}>Precio base</strong>
                <p style={styles.pizzaMagicText}>
                  Para empanadas con presentaciones, el cobro usa las filas de arriba.
                </p>
                <div style={styles.pizzaPriceRow}>
                  <span style={styles.pizzaPriceValue}>
                    {formatMoney(empanadaStartingPrice)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div style={styles.priceBox}>
          <h4 style={{ marginTop: 0, marginBottom: 10 }}>Precio del plato</h4>

          <label style={styles.radioOption}>
            <input
              type="radio"
              name="modoPrecio"
              value="suggested"
              checked={modoPrecio === "suggested"}
              onChange={() => setModoPrecio("suggested")}
            />
            <span>Precio sugerido por receta base</span>
          </label>

          <div style={styles.priceValue}>${precioSugerido.toFixed(2)}</div>
          <div style={styles.priceLegend}>
            Los extras posibles no se suman aca. El precio sugerido toma solo lo que el
            plato trae de base.
          </div>

          <label style={styles.radioOption}>
            <input
              type="radio"
              name="modoPrecio"
              value="manual"
              checked={modoPrecio === "manual"}
              onChange={() => setModoPrecio("manual")}
            />
            <span>Precio manual</span>
          </label>

          <input
            type="number"
            min="0"
            step="0.01"
            value={precioManual}
            onChange={(event) => setPrecioManual(event.target.value)}
            placeholder="Ingrese el precio manual"
            disabled={modoPrecio !== "manual"}
            style={{
              ...styles.input,
              marginBottom: 0,
              opacity: modoPrecio === "manual" ? 1 : 0.6,
            }}
          />
        </div>

        <div style={styles.recipeSummaryCard}>
          <div style={styles.recipeSummaryHeader}>
            <div>
              <h4 style={{ marginTop: 0, marginBottom: 4 }}>Receta operativa</h4>
              <p style={styles.ingredientesLegend}>
                Aqui defines exactamente cuanto consume cada ingrediente del plato.
                Puedes cargar `1 un`, `200 g`, `50 g`, `250 ml` y el descuento del stock
                respeta si el comensal lo quita o agrega extras.
              </p>
            </div>

            <div style={styles.ingredientesResumen}>
              <span style={styles.resumenChip}>{totalIngredientesBase} con base</span>
              <span style={styles.resumenChip}>{totalIngredientesExtra} con extras</span>
            </div>
          </div>

          {recipeSummaryItems.length > 0 ? (
            <div style={styles.recipeSummaryList}>
              {recipeSummaryItems.slice(0, 6).map((ingrediente) => (
                <div key={ingrediente.id} style={styles.recipeSummaryRow}>
                  <span style={styles.recipeSummaryName}>{ingrediente.nombre}</span>
                  <span style={styles.recipeSummaryMeta}>
                    {ingrediente.quantityLabel}
                    {ingrediente.canAddMore ? " - con extra" : ""}
                    {ingrediente.canRemove ? " - se puede quitar" : ""}
                  </span>
                </div>
              ))}
              {recipeSummaryItems.length > 6 ? (
                <div style={styles.inlineHint}>
                  +{recipeSummaryItems.length - 6} ingredientes mas cargados en la receta.
                </div>
              ) : null}
            </div>
          ) : (
            <div style={styles.inlineHint}>
              Todavia no cargaste la receta del plato. Abre la receta y marca el consumo
              exacto de cada ingrediente.
            </div>
          )}

          <div style={styles.recipeSummaryActions}>
            <button
              type="button"
              style={styles.recipeOpenButton}
              onClick={() => setShowRecipeModal(true)}
            >
              {recipeSummaryItems.length > 0 ? "Editar receta" : "Cargar receta"}
            </button>
          </div>
        </div>

        <div style={styles.modalButtons}>
          {saveError ? (
            <div style={styles.saveErrorBox}>{saveError}</div>
          ) : null}
          <button type="button" style={styles.btnCancel} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            style={styles.btnConfirm}
            onClick={handleGuardar}
            disabled={guardando || processingImage}
          >
            {processingImage ? "Optimizando imagen..." : guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      {showRecipeModal ? (
        <div style={styles.recipeModalOverlay}>
          <div style={styles.recipeModalContent}>
            <div style={styles.recipeModalHeader}>
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 6 }}>Receta operativa del plato</h3>
                <p style={styles.ingredientesLegend}>
                  Carga el consumo exacto por ingrediente. Si el stock esta en kilos y aqui
                  escribes gramos, la app hace la conversion automaticamente al descontar.
                </p>
              </div>
              <button
                type="button"
                style={styles.recipeCloseButton}
                onClick={() => setShowRecipeModal(false)}
              >
                Cerrar
              </button>
            </div>

            <div style={styles.ingredientesContainer}>
              <div style={styles.ingredientesHeader}>
                <div>
                  <h4 style={{ marginTop: 0, marginBottom: 4 }}>Ingredientes y consumos</h4>
                  <p style={styles.ingredientesLegend}>
                    Ejemplo: pan `1 un`, medallon `1 un`, lechuga `200 g`, queso `50 g`.
                  </p>
                </div>

                <div style={styles.ingredientesResumen}>
                  <span style={styles.resumenChip}>{totalIngredientesBase} con base</span>
                  <span style={styles.resumenChip}>{totalIngredientesExtra} con extras</span>
                </div>
              </div>

              <div style={styles.ingredientesToolbar}>
                <input
                  type="text"
                  value={busquedaIngrediente}
                  onChange={(event) => setBusquedaIngrediente(event.target.value)}
                  placeholder="Buscar ingrediente"
                  style={{ ...styles.input, marginBottom: 0 }}
                />
              </div>

              <div style={styles.ingredientesGrid}>
                {ingredientesFiltrados.map((ingrediente) => {
                  const config = recipeConfigById[ingrediente.id] || {
                    menuBaseQuantity: 0,
                    baseQuantity: 0,
                    consumptionQuantity: 0,
                    canRemove: false,
                    canAddMore: false,
                    maxExtraQuantity: 0,
                    consumptionUnit: DEFAULT_STOCK_UNIT,
                  };
                  const selectedUnit =
                    config.consumptionUnit || config.unit || config.unidad || DEFAULT_STOCK_UNIT;

                  return (
                    <div
                      key={ingrediente.id}
                      style={{
                        ...styles.ingredienteItem,
                        ...(config.menuBaseQuantity > 0 || config.canAddMore
                          ? styles.ingredienteItemActive
                          : {}),
                      }}
                    >
                      <div style={styles.ingredienteTop}>
                        <div style={styles.ingredienteInfo}>
                          <span style={styles.ingredienteNombre}>{ingrediente.nombre}</span>
                          <span style={styles.ingredienteEstado}>
                            {config.consumptionQuantity > 0
                              ? `Menu ${formatRecipeQuantity(
                                  config.menuBaseQuantity || 0
                                )} · Stock ${formatRecipeQuantity(
                                  config.consumptionQuantity || 0
                                )} ${selectedUnit}`
                              : "Sin consumo cargado"}
                          </span>
                        </div>

                        <span style={styles.ingredientePrecio}>
                          ${Number(ingrediente.precio || 0).toFixed(2)}
                        </span>
                      </div>

                      <div style={styles.controlSection}>
                        <div style={styles.recipeAmountRow}>
                          <div style={styles.recipeAmountField}>
                            <span style={styles.controlTitle}>Consumo base</span>
                            <input
                              type="number"
                              min="0"
                              step={getRecipeQuantityStep(selectedUnit)}
                              value={
                                Number(config.consumptionQuantity || 0) > 0
                                  ? String(config.consumptionQuantity)
                                  : ""
                              }
                              onChange={(event) =>
                                updateConsumptionQuantity(ingrediente.id, event.target.value)
                              }
                              placeholder="0"
                              style={styles.recipeQuantityInput}
                            />
                          </div>

                          <div style={styles.recipeUnitField}>
                            <span style={styles.controlTitle}>Unidad</span>
                            <select
                              value={selectedUnit}
                              onChange={(event) =>
                                setRecipeIngredient(ingrediente.id, (current) => ({
                                  ...current,
                                  consumptionUnit: normalizeStockUnit(event.target.value),
                                  unit: normalizeStockUnit(event.target.value),
                                  unidad: normalizeStockUnit(event.target.value),
                                }))
                              }
                              style={styles.recipeUnitSelect}
                            >
                              {STOCK_UNIT_OPTIONS.map((unidad) => (
                                <option key={unidad.value} value={unidad.value}>
                                  {unidad.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div style={styles.recipeAmountField}>
                            <span style={styles.controlTitle}>Base en menu</span>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={
                                Number(config.menuBaseQuantity || 0) > 0
                                  ? String(config.menuBaseQuantity)
                                  : ""
                              }
                              onChange={(event) =>
                                updateMenuBaseQuantity(ingrediente.id, event.target.value)
                              }
                              placeholder="0"
                              style={styles.recipeQuantityInput}
                            />
                          </div>
                        </div>

                        <div style={styles.inlineHint}>
                          El consumo base representa el total que descuenta la base visible del
                          menu. Si el cliente agrega o quita unidades, el descuento se ajusta en
                          proporcion.
                        </div>

                        <label
                          style={{
                            ...styles.checkboxRow,
                            opacity: config.menuBaseQuantity > 0 ? 1 : 0.55,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(config.canRemove)}
                            disabled={config.menuBaseQuantity <= 0}
                            onChange={(event) =>
                              toggleCanRemove(ingrediente.id, event.target.checked)
                            }
                          />
                          <span>Si el cliente lo quita, no se descuenta</span>
                        </label>

                        {ingrediente.seleccionable || config.canAddMore ? (
                          <>
                            <label style={styles.checkboxRow}>
                              <input
                                type="checkbox"
                                checked={Boolean(config.canAddMore)}
                                onChange={(event) =>
                                  toggleCanAddMore(ingrediente.id, event.target.checked)
                                }
                              />
                              <span>Permitir agregarlo como extra</span>
                            </label>

                            {config.canAddMore && (
                              <div style={styles.extraConfigRow}>
                                <span style={styles.controlTitle}>Maximo de extras</span>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={config.maxExtraQuantity || DEFAULT_MAX_EXTRA_QUANTITY}
                                  onChange={(event) =>
                                    updateMaxExtraQuantity(ingrediente.id, event.target.value)
                                  }
                                  style={styles.extraInput}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={styles.inlineHint}>
                            En Ingredientes esta marcado como no disponible para ofrecer
                            como extra.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {ingredientesFiltrados.length === 0 && (
                <div style={styles.sinResultados}>
                  No hay ingredientes que coincidan con esa busqueda.
                </div>
              )}
            </div>

            <div style={styles.recipeModalFooter}>
              <button
                type="button"
                style={styles.recipeOpenButton}
                onClick={() => setShowRecipeModal(false)}
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 12,
    maxWidth: 760,
    width: "92%",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    boxSizing: "border-box",
  },
  input: {
    width: "100%",
    padding: 10,
    marginBottom: 10,
    borderRadius: 8,
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: 10,
    marginBottom: 10,
    borderRadius: 8,
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: 8,
    borderRadius: 6,
    border: "1px solid #ccc",
    marginTop: 4,
    boxSizing: "border-box",
  },
  row: {
    display: "flex",
    gap: 8,
    marginTop: 8,
    flexWrap: "wrap",
  },
  previewImagen: {
    width: "100%",
    maxHeight: 220,
    objectFit: "cover",
    borderRadius: 10,
    marginBottom: 10,
    display: "block",
  },
  pizzaMagicBox: {
    marginBottom: 15,
    padding: 16,
    borderRadius: 14,
    border: "1px solid #fb923c",
    backgroundColor: "#fff7ed",
    boxSizing: "border-box",
  },
  pizzaMagicHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  pizzaMagicKicker: {
    display: "block",
    marginBottom: 4,
    color: "#c2410c",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  pizzaMagicTitle: {
    margin: 0,
    color: "#0f172a",
  },
  pizzaMagicText: {
    margin: "6px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.45,
  },
  pizzaMagicBadge: {
    padding: "7px 10px",
    borderRadius: 999,
    backgroundColor: "#ffedd5",
    border: "1px solid #fdba74",
    color: "#9a3412",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
  },
  pizzaMagicGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 12,
  },
  pizzaMagicCard: {
    padding: 12,
    borderRadius: 12,
    border: "1px solid #fed7aa",
    backgroundColor: "#ffffff",
  },
  pizzaMagicCardTitle: {
    display: "block",
    color: "#0f172a",
    marginBottom: 4,
  },
  pizzaPricingPanel: {
    padding: 12,
    borderRadius: 14,
    border: "1px solid #fed7aa",
    backgroundColor: "#ffffff",
    marginBottom: 12,
  },
  pizzaPricingHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  pizzaSizeTableHeader: {
    display: "grid",
    gridTemplateColumns: "70px minmax(140px, 1.2fr) repeat(4, minmax(90px, 1fr))",
    gap: 8,
    color: "#9a3412",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  pizzaSizeRows: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  pizzaSizeRow: {
    display: "grid",
    gridTemplateColumns: "70px minmax(140px, 1.2fr) repeat(4, minmax(90px, 1fr))",
    gap: 8,
    alignItems: "center",
  },
  empanadaSaleTableHeader: {
    display: "grid",
    gridTemplateColumns: "70px minmax(150px, 1fr) minmax(90px, 0.5fr) minmax(120px, 0.7fr)",
    gap: 8,
    color: "#9a3412",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  empanadaSaleRow: {
    display: "grid",
    gridTemplateColumns: "70px minmax(150px, 1fr) minmax(90px, 0.5fr) minmax(120px, 0.7fr)",
    gap: 8,
    alignItems: "center",
  },
  empanadaSaleLabel: {
    minHeight: 42,
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    borderRadius: 10,
    border: "1px solid #fed7aa",
    backgroundColor: "#fff7ed",
    color: "#0f172a",
  },
  empanadaSaleUnits: {
    minHeight: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: "1px solid #fed7aa",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    fontWeight: 800,
  },
  empanadaCookingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
  },
  empanadaCookingCard: {
    minHeight: 48,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #fed7aa",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontWeight: 800,
    cursor: "pointer",
  },
  empanadaCookingCardActive: {
    borderColor: "#fb923c",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
  },
  pizzaSizeRowInactive: {
    opacity: 0.58,
  },
  pizzaSizeCheckLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    borderRadius: 10,
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
  },
  pizzaSizeInput: {
    width: "100%",
    minWidth: 0,
    padding: "10px 11px",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    fontSize: 14,
    boxSizing: "border-box",
  },
  pizzaAddSizeRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 10,
    alignItems: "center",
    marginTop: 12,
  },
  pizzaSizeList: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 12,
  },
  pizzaSizeButton: {
    padding: "8px 10px",
    borderRadius: 999,
    border: "1px solid #fed7aa",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
  },
  pizzaSizeButtonActive: {
    backgroundColor: "#fb923c",
    borderColor: "#fb923c",
    color: "#ffffff",
  },
  pizzaPriceRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 12,
  },
  pizzaPriceValue: {
    fontSize: 18,
    fontWeight: 800,
    color: "#0f172a",
  },
  pizzaSecondaryButton: {
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #fb923c",
    backgroundColor: "#ffffff",
    color: "#c2410c",
    cursor: "pointer",
    fontWeight: 800,
  },
  pizzaHalfStatus: {
    display: "inline-flex",
    marginTop: 12,
    padding: "7px 10px",
    borderRadius: 999,
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: 800,
  },
  pizzaHalfPreview: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 10,
    color: "#334155",
    fontSize: 13,
    fontWeight: 700,
  },
  priceBox: {
    marginBottom: 15,
    padding: 12,
    border: "1px solid #d1d5db",
    borderRadius: 10,
    backgroundColor: "#f9fafb",
  },
  radioOption: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  priceValue: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 6,
    color: "#111827",
  },
  priceLegend: {
    marginBottom: 12,
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
  },
  recipeSummaryCard: {
    marginBottom: 15,
    padding: 16,
    borderRadius: 12,
    border: "1px solid #dbe2ea",
    backgroundColor: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  recipeSummaryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  recipeSummaryList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  recipeSummaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: "10px 12px",
    borderRadius: 10,
    backgroundColor: "#ffffff",
    border: "1px solid #dbe2ea",
  },
  recipeSummaryName: {
    fontWeight: 700,
    color: "#0f172a",
  },
  recipeSummaryMeta: {
    color: "#475569",
    fontSize: 13,
  },
  recipeSummaryActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  recipeOpenButton: {
    padding: "10px 14px",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  recipeModalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1001,
    padding: 16,
    boxSizing: "border-box",
  },
  recipeModalContent: {
    width: "100%",
    maxWidth: 1080,
    maxHeight: "88vh",
    overflowY: "auto",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.24)",
    padding: 20,
    boxSizing: "border-box",
  },
  recipeModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  recipeCloseButton: {
    padding: "10px 14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  recipeModalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 16,
  },
  ingredientesContainer: {
    marginBottom: 15,
    padding: 16,
    borderRadius: 12,
    border: "1px solid #dbe2ea",
    backgroundColor: "#f8fafc",
  },
  ingredientesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  ingredientesLegend: {
    margin: 0,
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.5,
  },
  ingredientesResumen: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  ingredientesToolbar: {
    marginBottom: 14,
  },
  resumenChip: {
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor: "#e2e8f0",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 600,
  },
  ingredientesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 12,
  },
  ingredienteItem: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 12,
    borderRadius: 12,
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
  },
  ingredienteItemActive: {
    borderColor: "#86b7fe",
    backgroundColor: "#f8fbff",
  },
  ingredienteTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
    width: "100%",
  },
  ingredienteInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  ingredienteNombre: {
    fontWeight: 700,
    color: "#0f172a",
  },
  ingredienteEstado: {
    color: "#64748b",
    fontSize: 13,
  },
  ingredientePrecio: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 700,
    backgroundColor: "#f8fafc",
    border: "1px solid #dbe2ea",
    borderRadius: 999,
    padding: "6px 10px",
    whiteSpace: "nowrap",
  },
  controlSection: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  recipeAmountRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(130px, 150px)",
    gap: 10,
    alignItems: "end",
  },
  recipeAmountField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  recipeUnitField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  controlTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#334155",
  },
  recipeQuantityInput: {
    width: "100%",
    padding: 10,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    boxSizing: "border-box",
    fontSize: 14,
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#334155",
    fontSize: 14,
  },
  extraConfigRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  extraInput: {
    width: 84,
    padding: 8,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    boxSizing: "border-box",
  },
  recipeUnitSelect: {
    minWidth: 120,
    padding: 8,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
  },
  inlineHint: {
    padding: "10px 12px",
    borderRadius: 10,
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
  },
  sinResultados: {
    marginTop: 12,
    padding: 12,
    borderRadius: 10,
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    backgroundColor: "#ffffff",
    textAlign: "center",
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  saveErrorBox: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    fontSize: 13,
    lineHeight: 1.45,
    boxSizing: "border-box",
  },
  btnCancel: {
    padding: "8px 14px",
    backgroundColor: "#ccc",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  btnConfirm: {
    padding: "8px 14px",
    backgroundColor: "#2ecc71",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  btnAdd: {
    padding: "8px 10px",
    backgroundColor: "#3498db",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  btnDelete: {
    padding: "8px 10px",
    backgroundColor: "#fff",
    color: "#dc2626",
    border: "1px solid #dc2626",
    borderRadius: 6,
    cursor: "pointer",
  },
};

export default ModalCrearPlato;
